// Native catalog adapter shared by the administrator UI and Worker.
const object = value => value && typeof value === 'object' && !Array.isArray(value);
export function catalogGroups(catalog) {
  const wrapper = Array.isArray(catalog) ? catalog[0] : catalog;
  const pack = wrapper?.result?.data?.[0];
  if (object(pack?.effects)) {
    const prefix = Array.isArray(catalog) ? [0] : [];
    return Object.entries(pack.effects).filter(([key, value]) => key !== 'meta' && object(value))
      .map(([key]) => ({path: [...prefix, 'result', 'data', 0, 'effects', key], label: key}));
  }
  if (Array.isArray(catalog?.commands)) return [{path: ['commands'], label: 'commands'}];
  if (object(catalog?.effects) || Array.isArray(catalog?.effects)) return [{path: ['effects'], label: 'effects'}];
  throw new Error('Unsupported catalog format');
}
export const atPath = (value, path) => path.reduce((node, key) => node[key], value);
export function catalogEntries(catalog) {
  return catalogGroups(catalog).flatMap(group => {
    const collection = atPath(catalog, group.path);
    return Object.entries(collection).map(([key, value]) => ({
      path: [...group.path, Array.isArray(collection) ? Number(key) : key],
      group: group.label, id: Array.isArray(collection) ? (value?.id || value?.command) : key, value
    }));
  });
}
export function replaceEntry(catalog, path, value) {
  const copy = structuredClone(catalog);
  atPath(copy, path.slice(0, -1))[path.at(-1)] = value;
  return copy;
}
export function addEntry(catalog, group, id, value) {
  const copy = structuredClone(catalog), collection = atPath(copy, group.path);
  if (catalogEntries(copy).some(entry => entry.id === id)) throw new Error('Duplicate command ID');
  if (Array.isArray(collection)) collection.push('id' in value || !('command' in value) ? {...value, id} : {...value, command: id});
  else Object.defineProperty(collection, id, {value, enumerable: true, writable: true, configurable: true});
  return copy;
}
export function validateGameCatalog(catalog) {
  try {
    // Reject prototype keys anywhere, including nested command parameters.
    const safe = value => !value || typeof value !== 'object' || Object.entries(value).every(([key, item]) =>
      !['__proto__', 'prototype', 'constructor'].includes(key) && safe(item));
    if (!safe(catalog)) return false;
    const entries = catalogEntries(catalog), ids = new Set();
    if (!entries.length || entries.length > 10000) return false;
    return entries.every(({id, value}) => {
      if (typeof id !== 'string' || !id.trim() || id.length > 200 || ids.has(id) || !object(value)) return false;
      ids.add(id);
      if (value.enabled !== undefined && typeof value.enabled !== 'boolean') return false;
      if (value.image) {
        const url = new URL(value.image);
        if (url.protocol !== 'https:' || url.username || url.password || value.image.length > 2048) return false;
      }
      if (value.name !== undefined && typeof value.name !== 'string' &&
          !(object(value.name) && Object.values(value.name).every(name => typeof name === 'string'))) return false;
      for (const key of ['quantity', 'duration']) {
        const range = value[key];
        if (!object(range)) continue;
        for (const field of ['min', 'max', 'value']) if (range[field] !== undefined && !Number.isFinite(range[field])) return false;
        if (range.min !== undefined && range.max !== undefined && range.min > range.max) return false;
        if (range.value !== undefined && (range.value < range.min || range.value > range.max)) return false;
      }
      return true;
    });
  } catch { return false; }
}
export function publicGameCatalog(catalog, gameId, managed = false) {
  const copy = structuredClone(catalog);
  if (managed) (Array.isArray(copy) ? copy[0] : copy)._catalogManaged = true;
  for (const group of catalogGroups(copy)) {
    const collection = atPath(copy, group.path);
    const enabled = Object.entries(collection).filter(([, value]) => value.enabled !== false)
      .map(([key, value]) => [key, managed ? {...value, i18nKey: `catalog.${gameId}.${value.id || value.command || key}`} : value]);
    atPath(copy, group.path.slice(0, -1))[group.path.at(-1)] = Array.isArray(collection)
      ? enabled.map(([, value]) => value) : Object.fromEntries(enabled);
  }
  return copy;
}
